/**
 * Classification result for a Proagrica sampling item: tells the import
 * pipeline which `Job.files` slot the fetched GeoJSON should land in.
 */
export type ProagricaSamplingItemKind = "zone" | "points" | "none";

/** The minimal shape this classifier needs from a Proagrica sampling item.
 *  Only `SoilSamplingRecords[0].WKT` is read; the full type lives in
 *  `@domain/Integrations/Proagrica`. */
export interface SamplingItemForClassification {
	SoilSamplingRecords?: ReadonlyArray<{ WKT?: string }>;
}

/**
 * Decides whether a Proagrica sampling item should produce a zone-based
 * job or a points-based job, based on whether the *first* soil-sampling
 * record's WKT contains the substring "polygon" (case-insensitive).
 *
 *  - WKT mentions polygon → `"zone"` (route GeoJSON to `zonesGeoJSON`).
 *  - WKT exists but no polygon → `"points"` (route to `pointsGeoJSON`).
 *  - No SoilSamplingRecords at all → `"none"` (skip; the job carries
 *    only its boundary).
 *
 * The substring check is intentionally permissive — Proagrica's WKT can
 * be `POLYGON(...)`, `MULTIPOLYGON(...)`, or wrapped in a
 * `GEOMETRYCOLLECTION(...)`; all three contain "polygon" and all three
 * should be treated as zone geometries. Pure: no IO, no Date.
 */
export function classifySamplingItem(item: SamplingItemForClassification | undefined): ProagricaSamplingItemKind {
	const first = item?.SoilSamplingRecords?.[0];
	if (!first || !first.WKT) return "none";
	return first.WKT.toLowerCase().includes("polygon") ? "zone" : "points";
}
